import React, { useState, useEffect, useRef } from "react";
import "./nav.css";
import {
  FaShoppingCart,
  FaUser,
  FaBars,
  FaTimes,
  FaPhoneAlt,
  FaDownload,
  FaBoxOpen,
  FaUserCircle
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import brochure from "../assets/brochure.pdf";
import {
  getSession,
  clearSession,
  isLoggedIn as checkLogin
} from "../utils/session";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(checkLogin());
  const [userName, setUserName] = useState(getSession().user?.name || "");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  /* ================= FETCH USER ================= */
  useEffect(() => {
    if (!checkLogin()) {
      setIsLoggedIn(false);
      setUserName("");
      return;
    }
    const sessionUser = getSession().user;
    setUserName(sessionUser?.name || "");
  }, [isLoggedIn]);

  /* ================= FETCH CART COUNT ================= */
  useEffect(() => {
    const fetchCartCount = async () => {
      try {
        const res = await fetch(
          "https://sangitshreeprakashan.com/backend/api/getcart.php",
          { method: "GET", credentials: "include" }
        );

        if (res.status === 401) {
          clearSession();
          setIsLoggedIn(false);
          setUserName("");
          return;
        }

        const data = await res.json();
        if (data.status === "success") {
          const total = data.data.reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0
          );
          setCartCount(total);
        } else {
          setCartCount(0);
        }
      } catch (err) {
        console.error("Cart count error:", err);
      }
    };

    fetchCartCount();
    const handleCartUpdate = () => fetchCartCount();
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  /* ================= SYNC AUTH ACROSS TABS ================= */
  useEffect(() => {
    const syncAuth = () => {
      const logged = checkLogin();
      setIsLoggedIn(logged);
      setUserName(logged ? getSession().user?.name || "" : "");
    };
    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, []);

  /* ================= OUTSIDE CLICK CLOSE ================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        userMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  /* ================= CART CLICK ================= */
  const handleCartClick = () => {
    if (!checkLogin()) {
      clearSession();
      setIsLoggedIn(false);
      navigate("/login");
      return;
    }
    navigate("/cart");
  };

  /* ================= USER ICON CLICK ================= */
  const handleUserClick = async () => {
    if (!checkLogin()) {
      clearSession();
      setIsLoggedIn(false);
      setUserName("");
      setUserMenuOpen(false);
      navigate("/login");
      return;
    }

    setUserMenuOpen((prev) => !prev);
  };

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
    try {
      await fetch(
        "https://sangitshreeprakashan.com/backend/api/logout.php",
        { method: "POST", credentials: "include" }
      );
    } catch {}

    clearSession();
    setIsLoggedIn(false);
    setUserName("");
    setUserMenuOpen(false);
    navigate("/login");
  };

  return (
    <nav className="glass-navbar">
      <div className="navbar-container">
        {/* LEFT: LOGO & PHONE */}
        <div className="navbar-left">
          <Link to="/" className="logo-link">
            <img src="/logo.png" alt="Sangit Shree Prakashan" className="logo-img" />
          </Link>
          <a href="tel:+917408452828" className="nav-phone-link" title="Call us">
            <FaPhoneAlt className="nav-phone-icon" />
            <span className="nav-phone-text">+91 7408452828</span>
          </a>
        </div>

        {/* CENTER: NAV LINKS */}
        <div className={`nav-content ${menuOpen ? "show" : ""}`}>
          <ul className="nav-links">
            <li><Link to="/" onClick={() => setMenuOpen(false)}>Home</Link></li>
            <li><Link to="/book" onClick={() => setMenuOpen(false)}>Books</Link></li>
            <li><Link to="/about" onClick={() => setMenuOpen(false)}>About</Link></li>
            <li><Link to="/gallery" onClick={() => setMenuOpen(false)}>Gallery</Link></li>
            <li><Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link></li>
          </ul>

          {/* Mobile drawer actions */}
          <div className="mobile-nav-actions">
            <a href="tel:+917408452828" className="mobile-phone-link">
              <FaPhoneAlt className="nav-phone-icon" /> +91 7408452828
            </a>
            <a
              href={brochure}
              download="Sangit_Shree_Prakashan_Brochure.pdf"
              className="mobile-brochure-btn"
              onClick={() => setMenuOpen(false)}
            >
              <FaDownload /> Download Brochure
            </a>
          </div>
        </div>

        {/* RIGHT: BROCHURE, ICONS, MENU TOGGLE */}
        <div className="navbar-right">
          <a
            href={brochure}
            download="Sangit_Shree_Prakashan_Brochure.pdf"
            className="nav-brochure-btn"
          >
            <FaDownload className="btn-icon" />
            <span>Download Brochure</span>
          </a>

          <div className="icon-buttons">
            <button
              className="icon-btn neon cart-icon-wrapper"
              onClick={handleCartClick}
              aria-label="Shopping Cart"
            >
              <FaShoppingCart />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>

            {isLoggedIn ? (
              <div className="user-dropdown-wrapper" ref={menuRef}>
                <button
                  className="icon-btn neon"
                  onClick={handleUserClick}
                  aria-label="User Account"
                >
                  <FaUser />
                </button>

                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div className="user-row">
                      <span className="user-greet">Hello,</span>
                      <strong className="user-name">
                        {userName || "User"}
                      </strong>
                    </div>
                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <FaUserCircle className="dropdown-icon" /> Profile
                    </Link>
                    <Link
                      to="/history"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <FaBoxOpen className="dropdown-icon" /> Order History
                    </Link>
                    <button className="logout-btn" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <button className="icon-btn neon" aria-label="Login">
                  <FaUser />
                </button>
              </Link>
            )}

            {/* HAMBURGER TOGGLE */}
            <div
              className="menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle navigation"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
